import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { DwDateTimeInputOptions } from '@devwareapps/devware-cap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

export type PresetDateTimeOptionKey = 'tomorrow' | 'in-3-days' | 'next-week';

export interface PresetDateTimeControlOptions extends DwDateTimeInputOptions {
  defaultPresetOnAdd?: PresetDateTimeOptionKey | false;
  presetTime?: string;
  presetHour?: number;
  presetMinute?: number;
}

@Component({
  selector: 'app-preset-date-time-form-control-config',
  templateUrl: './preset-date-time-form-control-config.component.html'
})
export class PresetDateTimeFormControlConfigComponent implements OnChanges, OnDestroy {
  @Input() controlOptions: PresetDateTimeControlOptions;
  @Input() itemName: string;
  @Input() fieldConfig: any;
  @Input() attribute: any;
  @Output() controlOptionsChange = new EventEmitter<PresetDateTimeControlOptions>();

  readonly timeForm = new FormGroup({
    presetTime: new FormControl('05:00 PM')
  });

  readonly defaultPresetOptions: { label: string; value: PresetDateTimeOptionKey | false }[] = [
    { label: 'None', value: false },
    { label: 'Tomorrow', value: 'tomorrow' },
    { label: 'In 3 days', value: 'in-3-days' },
    { label: 'Next week', value: 'next-week' },
  ];

  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.timeForm.get('presetTime').valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!this.controlOptions) {
          return;
        }

        this.controlOptions = this.normalizeOptions({
          ...this.controlOptions,
          presetTime: value
        });
        this.emitChange();
      });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.controlOptions) {
      this.controlOptions = this.normalizeOptions(this.controlOptions);
      this.patchTimeControl();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  setupControl(): void {
    this.controlOptions = this.normalizeOptions(this.controlOptions);
    this.patchTimeControl();
    this.emitChange();
  }

  onChange(): void {
    this.controlOptions = this.normalizeOptions(this.controlOptions);
    this.emitChange();
  }

  private normalizeOptions(options: PresetDateTimeControlOptions): PresetDateTimeControlOptions {
    const timeParts = this.parseTime(options?.presetTime);

    return {
      ...(options || {}),
      presetTime: options?.presetTime || '05:00 PM',
      presetHour: timeParts.hour,
      presetMinute: timeParts.minute,
    };
  }

  private patchTimeControl(): void {
    this.timeForm.get('presetTime').setValue(this.controlOptions?.presetTime || '05:00 PM', { emitEvent: false });
  }

  private parseTime(value: string): { hour: number; minute: number } {
    const trimmedValue = `${value || '05:00 PM'}`.trim();
    const match = trimmedValue.match(/^(\d{1,2}):(\d{2})(?:\s*([AP]M))?$/i);

    if (!match) {
      return { hour: 17, minute: 0 };
    }

    const minute = Math.max(0, Math.min(59, Number(match[2])));
    let hour = Math.max(0, Math.min(23, Number(match[1])));
    const amPm = match[3]?.toUpperCase();

    if (amPm === 'PM' && hour < 12) {
      hour += 12;
    }

    if (amPm === 'AM' && hour === 12) {
      hour = 0;
    }

    return { hour, minute };
  }

  private emitChange(): void {
    this.controlOptionsChange.emit({ ...this.controlOptions });
  }
}
